<template>
  <header class="header" :data-position="position">
    <nav class="navbar navbar-expand-lg" :class="[position == 'body' ? 'navbar-light' : 'navbar-dark']">
      <div class="container">
        <a class="navbar-brand" :href="route('index')" @click.prevent="goHome">
          <img :src="'/images/logo/logo' + (position == 'body' ? '' : '-white') + '.svg'" class="logo" alt="Veximus Technologies" />
        </a>
        <button class="navbar-toggler" data-bs-toggle="collapse" data-bs-target="#navbar" aria-controls="navbar" :aria-label="t('Menu')" ref="toggler">
          <span class="navbar-toggler-icon"></span>
        </button>
        <div class="navbar-collapse collapse" id="navbar" ref="collapsible">
          <ul class="navbar-nav ms-auto">
            <li class="nav-item">
              <a :href="route('index')" @click.prevent="goHome">
                {{ t('Home') }}
              </a>
            </li>
            <li class="nav-item dropdown">
              <a class="dropdown-toggle" href="#" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                {{ t('Services') }}
                <font-awesome-icon class="dropdown-caret" icon="chevron-down" aria-hidden="true" />
              </a>
              <div class="dropdown-menu dropdown-menu-center">
                <ul class="dropdown-menu-list">
                  <li>
                    <Link class="dropdown-item" :href="route('web-design')" @click="closeNav">
                      {{ t('Web design') }}
                    </Link>
                  </li>
                  <li>
                    <Link class="dropdown-item" :href="route('digital-marketing')" @click="closeNav">
                      {{ t('Digital marketing') }}
                    </Link>
                  </li>
                  <li>
                    <Link class="dropdown-item" :href="route('mobile-application')" @click="closeNav">
                      {{ t('Mobile application') }}
                    </Link>
                  </li>
                  <li>
                    <Link class="dropdown-item" :href="route('ai-solutions')" @click="closeNav">
                      {{ t('AI Solutions') }}
                    </Link>
                  </li>
                  <li>
                    <Link class="dropdown-item" :href="route('brand-presence')" @click="closeNav">
                      {{ t('Brand presence') }}
                    </Link>
                  </li>
                </ul>
              </div>
            </li>
            <li class="nav-item">
              <Link :href="route('portfolios')" @click="closeNav">
                {{ t('Portfolios') }}
              </Link>
            </li>
            <li class="nav-item">
              <Link :href="route('promotions')" @click="closeNav">
                {{ t('Promotions') }}
              </Link>
            </li>
            <li class="nav-item">
              <Link :href="route('blog.index')" @click="closeNav">
                {{ t('Blog') }}
              </Link>
            </li>
            <li class="nav-item">
              <!-- The enquiry band is on every public page, so this stays on
                   the page the reader is already on. A root path here would
                   leave for home and only then scroll. -->
              <a href="#cta" @click.prevent="goCta">
                {{ t('Get in touch') }}
              </a>
            </li>

            <!-- The language rail is hidden below 768px, so the collapsed menu
                 carries the switcher as the list's final row. It is the same
                 anatomy as the rows above it, and expands the way Services
                 does, but its right column holds the current locale rather than
                 an index: the row exists to report a state, so a number there
                 would sit where the answer belongs. Above 768px the rail
                 serves, so the row stands down. -->
            <li class="nav-item dropdown nav-item--language d-md-none">
              <a
                class="dropdown-toggle"
                href="#"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <span class="nav-toggle-label">
                  {{ t('Language') }}
                  <font-awesome-icon class="dropdown-caret" icon="chevron-down" aria-hidden="true" />
                </span>
                <span class="nav-item-value">{{ currentLocaleAbbr }}</span>
              </a>
              <div class="dropdown-menu">
                <ul class="dropdown-menu-list">
                  <li v-for="option in locales" :key="option.code">
                    <!-- A link to this page in that language. `closeNav` is
                         explicit for the same reason every Services item carries
                         it: Bootstrap leaves the menu open when the clicked item
                         is not a toggle. The button is only the signed preview,
                         whose path is the signature and so cannot change. -->
                    <Link
                      v-if="hrefFor(option.code)"
                      class="dropdown-item language-option"
                      :class="{ 'is-active': option.code === locale }"
                      :href="hrefFor(option.code)"
                      @click="closeNav()"
                    >
                      {{ option.name }}
                      <font-awesome-icon
                        v-if="option.code === locale"
                        icon="check"
                        aria-hidden="true"
                      />
                    </Link>
                    <button
                      v-else
                      class="dropdown-item language-option"
                      :class="{ 'is-active': option.code === locale }"
                      type="button"
                      @click="setLocale(option.code); closeNav()"
                    >
                      {{ option.name }}
                      <font-awesome-icon
                        v-if="option.code === locale"
                        icon="check"
                        aria-hidden="true"
                      />
                    </button>
                  </li>
                </ul>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  </header>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

.header {
  color: $on-dark-strong;
  left: 0;
  padding: 1rem 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1010;

  .logo {
    height: 40px;
  }

  .navbar-nav {
    .nav-item {
      counter-increment: number;
      margin: 1rem 1rem 0;
      position: relative;

      &::before {
        content: '0' counter(number);
        font-size: 0.75em;
        opacity: 0.75;
        position: absolute;
        right: 0;
        top: -1rem;
      }

      > a {
        color: $on-dark-strong;
        font-family: $headings-font-family;
        font-weight: 500;
      }

      /* The caret is a font icon rather than Bootstrap's CSS border triangle.
         The toggle is a flex row so the icon centres on the text line and the
         gap sets the spacing; Font Awesome ships the glyph at a single weight,
         so the stroke is what thickens it. */
      .dropdown-toggle {
        align-items: center;
        display: inline-flex;
        gap: 0.3rem;

        &::after {
          display: none;
        }

        .dropdown-caret {
          font-size: 0.7rem;
          stroke: currentColor;
          stroke-linejoin: round;
          stroke-width: 40;
          transition: transform $dur $ease;
        }

        &.show .dropdown-caret {
          transform: rotate(180deg);
        }
      }
    }

    .dropdown-menu {
      border: none;
      border-radius: $r-sm;
      box-shadow: $shadow-card;
      margin-top: 0.75rem;
      min-width: 14rem;
      padding: 0.5rem 0;

      .dropdown-item {
        color: $text-strong;
        font-family: $headings-font-family;
        font-weight: 500;
        padding: 0.55rem 1.25rem;

        &:hover,
        &:focus {
          background: $tint-primary-soft;
          color: $primary;
        }
      }
    }

    /* The list is a plain ul, held out of the panel. Bootstrap expects
       `.dropdown-menu` itself to be the ul, and gave it the list resets to match.
       On mobile it cannot be: the panel is the box that expands, and
       `grid-template-rows` collapses only a single child. So the panel is a div
       and the items live here, in the element that also does the clipping. */
    .dropdown-menu-list {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    .dropdown-menu-center {
      @media (min-width: 992px) {
        left: 50%;
        right: auto;
        transform: translateX(-50%);

        &[data-bs-popper] {
          left: 50%;
          right: auto;
          transform: translateX(-50%);
        }
      }
    }
  }

  /* The language row. It is a nav row like Services and expands the same way,
     so everything about it is inherited. Two things are added: its right column
     is a value rather than an index, and that value has to reach the right edge.

     The counter is suppressed rather than left running behind the value. Every
     other row numbers itself, so the row that does not gets no number at all
     instead of a hidden one. `.navbar-nav` is in the selector because the rule
     that draws the number is `.navbar-nav .nav-item::before`; matching its depth
     is what lets `content: none` win.

     `> a.dropdown-toggle` rather than a bare `> a`, and `display: flex` rather
     than nothing, both for the same reason. `.navbar-nav .nav-item
     .dropdown-toggle` sets `display: inline-flex`, and it carries one more class
     than the mobile `> a` rule, so it wins on specificity and the row stayed
     inline-level. Inside a block `li` that is a shrink-to-fit box, which left
     `space-between` with no free space to distribute and parked the value right
     next to the caret. Naming the toggle class here takes the selector one class
     deeper than the rule that was winning, which is what makes the row a block
     and gives `space-between` something to work with. */
  .navbar-nav .nav-item--language {
    &::before {
      content: none;
    }

    > a.dropdown-toggle {
      display: flex;
      justify-content: space-between;
    }
  }

  /* The label and its caret as one unit, so the row's `space-between` pushes
     the value to the right edge without the caret drifting to the middle. */
  .nav-toggle-label {
    align-items: center;
    display: inline-flex;
    gap: 0.3rem;
  }

  /* The current locale, in the column the other rows number themselves in, at
     the same spec those numerals use: 0.75em at 75% ink. Matching the column
     rather than the $label-* eyebrow step is deliberate. The step is uppercase
     and 0.78rem, which would make this one row's right column heavier than the
     six above it, and a locale name is an endonym rather than a label. */
  .nav-item-value {
    font-size: 0.75em;
    opacity: 0.75;
  }

  /* A choice row. It is a link, the same as every other dropdown row, because
     choosing a language is a visit. The signed preview is the one button, and
     the same rules size both. The global button reset already gives a button
     `background: none`, `border: none` and `font: inherit`, so all that is added
     is what it does not do on its own: fill the row and put its label at the
     start.

     Keyboard focus is the `:focus` treatment the dropdown rows already carry,
     the fill and accent from `.dropdown-item` above, so the row is reachable
     without a second focus affordance that only this row would have. */
  .language-option {
    align-items: center;
    display: flex;
    gap: 0.75rem;
    justify-content: space-between;
    text-align: left;
    width: 100%;

    .svg-inline--fa {
      font-size: 0.7rem;
    }
  }

  /* The chosen language keeps the accent the hover state already uses on the
     same ground, so a row under the pointer never changes colour when it is also
     the active one.

     `.navbar-nav` is in the selector on purpose. The ground rules below are
     `.navbar-nav .dropdown-menu .dropdown-item`, and at the top of the page that
     one carries the attribute selector, so a shorter active rule would tie with
     it and lose on source order. Matching its depth wins on specificity, which
     is what the rule is doing rather than relying on where it sits. */
  .navbar-nav .nav-item--language .language-option.is-active {
    color: $primary;
    font-weight: 600;
  }

  &[data-position='top'] .navbar-nav .nav-item--language .language-option.is-active {
    color: $dark-accent;
  }

  &[data-position='body'] {
    backdrop-filter: blur(2.5rem);
    background: $header-surface-pinned;
    box-shadow: $shadow-card;
    color: $text-strong;
    padding: 0.55rem 0;
    position: fixed;
    top: 0;

    .logo {
      height: 34px;
    }

    .navbar-nav {
      .nav-item {
        margin-top: 0.75rem;

        &::before {
          top: -0.75rem;
        }

        > a {
          color: $text-strong;

          &:hover,
          &:focus {
            color: $primary;
          }
        }
      }
    }
  }

  @media (max-width: 991.98px) {
    backdrop-filter: blur(0.5rem);
    background: $header-surface;
    box-shadow: $shadow-card;

    /* The collapsed panel is the whole menu, and with the Services submenu open
       it can be taller than a phone viewport. Bounded, it scrolls instead of
       running off the screen under a bar that is fixed once it pins.

       `overscroll-behavior: contain` keeps that scroll inside the panel: without
       it, a flick that reached the bottom of the menu chains to the page, which
       fires the window scroll that closes the menu. Paging to the end of the
       list should not be what dismisses it. */
    .navbar-collapse.show {
      max-height: calc(100dvh - 4.5rem);
      overscroll-behavior: contain;
      overflow-y: auto;
    }

    .navbar-nav {
      padding-top: 1rem;

      .nav-item {
        /* The menu rows were bare text, about a 20px target. The whole row is
           the target now, which is also what makes the list read as a list. */
        > a {
          align-items: center;
          display: flex;
          padding: 0.85rem 0;
        }
      }

      /* The submenu expands instead of appearing. It is the site's collapse,
         from _mixins.scss, which is also what the assistant's chip row and
         reasoning trace use; the mechanic and the reasons for it live there.
         What is the header's is the open state and the inset.

         The panel's own margin and padding are zero while it is closed and return
         with the open state. Both sit outside the grid track, so leaving them
         would hold a closed menu open by about a rem and leave a gap under the
         row above.

         $dur is the caret's own duration, so the panel and the caret that
         answers to it move as one gesture. */
      .dropdown-menu {
        @include collapse($open: '&.show', $also: (margin $dur $ease, padding $dur $ease));

        margin: 0;
        /* The desktop menu is a floating panel and can afford 14rem; inline in
           the collapsed menu it is a block in a column and must not force one. */
        min-width: 0;
        /* Zero only so a closed panel cannot be held open by its own inset; the
           open state puts it back. */
        padding: 0;
        position: static;

        &.show {
          margin: 0.35rem 0 0.5rem;
          padding: 0.5rem 0;
        }
      }
    }

    /* The number above each row tracks the link's own padding, so it stays on
       the row's first line in both header states. */
    .navbar-nav .nav-item::before,
    &[data-position='body'] .navbar-nav .nav-item::before {
      top: 0.85rem;
    }
  }

  /* The dropdown's row response follows the ground the bar is standing on, and it
     is the same at both breakpoints. At `top` the bar sits over the dark hero, so
     a row carries the accent in its type over a neutral chip fill, which is the
     treatment PanelList's dark rows use. Pinned, the bar is the light gradient and
     the base rule above already has the light response: accent type over the soft
     primary tint.

     Declared once, outside both breakpoints, because the only thing that genuinely
     differs between them is the surface the rows sit on, and that is what the two
     blocks below set. The rule used to be written inside the mobile block only, so
     the two breakpoints were free to disagree about what a row looks like on dark.

     The attribute selector carries more weight than the base rule's `.dropdown-menu
     .dropdown-item`, so this wins on specificity rather than on source order. */
  &[data-position='top'] .navbar-nav .dropdown-menu {
    .dropdown-item {
      color: $on-dark-body;

      &:hover,
      &:focus {
        background: $on-dark-fill-strong;
        color: $dark-accent;
      }
    }
  }

  /* Desktop keeps a surface of its own, because here the menu leaves the bar and
     floats over the page rather than sitting inside it. It takes the bar's own
     ground in both states, so an open menu reads as part of the bar instead of a
     slab hanging under one, and it carries the same blur the bar does.

     At `top` that ground is the dark translucent panel the collapsed bar uses, so
     the site has one dark surface rather than two that happen to match. Pinned,
     it is the light gradient taken a stop more opaque than the bar's own, because
     an open menu has to stay readable over whatever it covers. Both come from the
     pair in _tokens.scss, so no fill is written out here.

     The guard is the same 992px the navbar itself collapses at. */
  @media (min-width: 992px) {
    &[data-position='top'] .navbar-nav .dropdown-menu {
      backdrop-filter: blur(0.5rem);
      background: $header-surface;
    }

    &[data-position='body'] .navbar-nav .dropdown-menu {
      backdrop-filter: blur(0.5rem);
      background: $header-surface-pinned-menu;
    }
  }

  /* Mobile: the collapsed panel is the header itself, so the menu has no ground of
     its own to float on and gives its surface up entirely. The bar is the dark
     translucent panel while it sits over the hero, and the light gradient once it
     pins; with no fill, no border and no shadow the menu is that same surface
     rather than a rectangle laid on top of it.

     It used to keep Bootstrap's white card in both, which is right on neither: a
     white slab inside the dark bar, and a slab brighter than the gradient it sat in
     once the bar pinned. */
  @media (max-width: 991.98px) {
    .navbar-nav .dropdown-menu {
      background: transparent;
      border: 0;
      box-shadow: none;
    }
  }
}
</style>

<script>
import { Inertia } from '@inertiajs/inertia';
import { Link } from '@inertiajs/inertia-vue3';
import { useI18n } from 'vue-i18n';
import { scrollToId } from '@/smoothScroll';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { LOCALES, localeHref, switchLocale } from '@/i18n/locales';

FontAwesomeLibrary.add(faCheck);

export default {
  components: {
    Link
  },

  data() {
    return {
      position: 'top'
    };
  },

  computed: {
    /* The locale the composer is actually on, so the row cannot claim a
       language the page is not rendering. */
    locale() {
      return this.$i18n.global.locale.value;
    },

    /* The right column carries the two-letter mark, not the full name, because it
       occupies the same slot the other rows put a two-digit index in. The chooser
       below it uses the full name. */
    currentLocaleAbbr() {
      const option = LOCALES.find((item) => item.code === this.locale);
      return option ? option.abbr : '';
    }
  },

  methods: {
    setLocale(code) {
      switchLocale(this, code);
    },

    hrefFor(code) {
      return localeHref(code);
    },

    /* Called on every scroll event, and deliberately not debounced.

       The open menu has to get out of the way as soon as the reader starts
       moving the page. A debounced close would wait for the scrolling to stop
       and only then hide it, which is the one moment it is no longer in the
       way. Only the position below is debounced, because that is the value that
       flips once and does not want to be recomputed per frame.

       Scrolling *inside* the open panel does not come through here: that is the
       element's own scroll, not the window's, so a reader paging down a long
       menu keeps it open. */
    handleScroll() {
      this.closeNav();
      this.syncPosition();
    },

    syncPosition: _.debounce(function () {
      this.position = window.scrollY < 100 ? 'top' : 'body';
    }, 50),

    closeNav() {
      var el = this.$refs.collapsible;
      if (el && el.classList.contains('show') && window.bootstrap && window.bootstrap.Collapse) {
        window.bootstrap.Collapse.getOrCreateInstance(el).hide();
      }
    },

    goHome() {
      this.closeNav();
      Inertia.visit(route('index'));
    },

    goCta() {
      this.closeNav();
      scrollToId('cta');
    }
  },

  created() {
    window.addEventListener('scroll', this.handleScroll);
  },

  mounted() {
    this.handleScroll();
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          Language: 'Language',
        },
        'zh-MY': {
          Language: '语言',
        },
        'ms-MY': {
          Language: 'Pilihan Bahasa',
        }
      }
    });

    return { t, locales: LOCALES };
  }
};
</script>
